import { useLayoutEffect, useState, type RefObject } from "react";

/** Places recipient content on canonical pages. Editing controls are excluded. */
export function useDocumentPagination(pageRef: RefObject<HTMLElement | null>, enabled: boolean, rowCount: number, contentKey: string) {
  const [breaks, setBreaks] = useState<number[]>([]);
  useLayoutEffect(() => {
    if (!enabled) { setBreaks(current => current.length ? [] : current); return; }
    const first = pageRef.current;
    const stage = first?.parentElement;
    if (!first || !stage) return;
    const measure = () => {
      const pages = [...stage.querySelectorAll<HTMLElement>(".document-page")];
      const firstBox = first.getBoundingClientRect();
      const scale = firstBox.width / first.offsetWidth;
      const footer = pages.at(-1)?.querySelector<HTMLElement>(".document-page-footer");
      const summary = pages.at(-1)?.querySelector<HTMLElement>(".document-page-summary");
      const reference = pages.at(-1)?.querySelector<HTMLElement>(".document-reference");
      const heading = first.querySelector<HTMLElement>(".document-line-heading");
      if (!footer || !summary || !heading) return;
      const safeBottom = firstBox.height - 44 * scale - footer.getBoundingClientRect().height - 16 * scale;
      const firstStart = heading.getBoundingClientRect().bottom - firstBox.top;
      const nextPage = pages[1];
      const nextHeading = nextPage?.querySelector<HTMLElement>(".document-line-heading");
      const nextStart = nextPage && nextHeading
        ? nextHeading.getBoundingClientRect().bottom - nextPage.getBoundingClientRect().top
        : (64 + 30 + 44 + 23.25) * scale;
      const totals = summary.querySelector<HTMLElement>(".document-totals");
      const notes = summary.querySelector<HTMLElement>(".document-notes");
      if (!totals || !notes) return;
      const notesContent = [...notes.children].filter(child => !child.classList.contains("invoice-print-empty"));
      const notesHeight = notesContent.length ? Math.max(...notesContent.map(child => child.getBoundingClientRect().height)) + parseFloat(getComputedStyle(notes).marginTop) * scale : 0;
      const summaryHeight = totals.getBoundingClientRect().height + parseFloat(getComputedStyle(totals).marginTop) * scale + notesHeight
        + (reference ? reference.getBoundingClientRect().height + parseFloat(getComputedStyle(reference).marginTop) * scale : 0);
      const heights = Array.from({ length: rowCount }, (_, index) => {
        const row = stage.querySelector<HTMLElement>(`[data-document-row][data-row-index="${index}"]`);
        if (!row) return 0;
        if (!row.closest(".invoice-document--editing")) return row.getBoundingClientRect().height;
        const title = row.querySelector<HTMLElement>(".document-line-title");
        const details = row.querySelector<HTMLElement>(".document-rich-text");
        const descriptionHeight = title ? (details?.textContent?.trim() ? details.getBoundingClientRect().bottom - title.getBoundingClientRect().top : title.getBoundingClientRect().height) : 0;
        const rate = row.querySelector<HTMLElement>(".document-rate-cell > :first-child");
        const valuesHeight = Math.max(rate?.getBoundingClientRect().height ?? 0, ...[...row.children].slice(2, 4).map(cell => cell.getBoundingClientRect().height));
        const style = getComputedStyle(row);
        return Math.max(descriptionHeight, valuesHeight) + (parseFloat(style.paddingTop) + parseFloat(style.paddingBottom) + parseFloat(style.borderBottomWidth)) * scale;
      });
      if (heights.some(height => height === 0)) return;
      const nextBreaks: number[] = [];
      let used = firstStart;
      let countOnPage = 0;
      for (let index = 0; index < rowCount; index += 1) {
        if (used + heights[index]! > safeBottom && countOnPage > 0) {
          nextBreaks.push(index);
          used = nextStart;
          countOnPage = 0;
        }
        used += heights[index]!;
        countOnPage += 1;
      }
      if (used + summaryHeight > safeBottom) nextBreaks.push(rowCount);
      setBreaks(current => current.length === nextBreaks.length && current.every((value, index) => value === nextBreaks[index]) ? current : nextBreaks);
    };
    measure();
    const observer = new ResizeObserver(measure);
    stage.querySelectorAll<HTMLElement>("[data-document-row], .document-page-summary, .document-page-header, .document-parties").forEach(element => observer.observe(element));
    return () => observer.disconnect();
  }, [pageRef, enabled, rowCount, contentKey, breaks.join(",")]);
  return breaks;
}
